import { useEffect }                             from 'react';
import { useMemo }                               from 'react';
import { createBrowserRouter }                   from 'react-router-dom';
import { RouterProvider as ReactRouterProvider } from 'react-router-dom';
import AuthLayout                                from '@widesk-ui/layouts/AuthLayout';
import AppAuthLayout                             from '@/components/AppAuthLayout';
import { useAuth }                               from '@widesk-ui/hooks/useAuth';
import LoginPage                                 from '@/pages/Login/LoginPage';
import Layout                                    from '@widesk-ui/layouts/Layout';
import LayoutSider                               from '@/components/LayoutSider';
import LayoutHeader                              from '@/components/LayoutHeader';
import BreadcrumbBar                             from '@widesk-ui/components/BreadcrumbBar';
import RouteErrorPage                            from '@/components/RouteErrorPage';
import View                                      from '@widesk-ui/components/View';
import Button                                    from '@widesk-ui/components/Button';
import useTheme                                  from '@widesk-ui/hooks/useTheme';
import LogoLoader                                from '@/components/LogoLoader';
import useDesignTokens                           from '@/hooks/useDesignTokens';
import AccountWall                               from './components/AccountWall';
import useAccount                                from './hooks/useAccount';
import ImpersonatePage                           from './pages/Impersonate';
import ImpersonateLogged                         from './pages/Impersonate/ImpersonateLogged';
import EditorPage                                from '@/pages/Editor/EditorPage';
import ValidateEmailChangePage                   from '@/pages/services/account/ValidateEmailChangePage';
import InvitationAcceptPage                       from '@/pages/services/account/InvitationAcceptPage';
import { notificationsSidebar }                  from '@stonecode/notifications-sidebar';

// On charge tous les fichiers "Route" du projet
const publicModules = import.meta.glob([
	'./routes/public/*Route.ts',
	'./routes/public/*Route.tsx',
], { eager: true });

const privateModules = import.meta.glob([
	'./routes/private/*Route.ts',
	'./routes/private/*Route.tsx',
	'./routes/private/services/*/*Route.ts',
	'./routes/private/services/*/*Route.tsx',
], { eager: true });

const createPublicRouter = () => createBrowserRouter([
	{
		element: <Layout />,
		children: [
			{
				path: '/impersonate/:urn',
				element: (
					<ImpersonatePage />
				),
			},
			...Object.values(publicModules).flatMap((module: any) => module.default),
			{
				path: '*',
				element: (
					<LoginPage />
				),
			},
		],
	},
]);

const createPrivateRouter = () => createBrowserRouter([
	{
		element: (
			<AppAuthLayout
				onLocationChange={notificationsSidebar.watchTrigger}
				initialCollapsed
				renderHeader={() => <LayoutHeader />}
				renderSider={collapsedState => <LayoutSider collapsedState={collapsedState} />}
				renderTopPage={() => <BreadcrumbBar />}
			/>
		),
		children: [
			{
				path: '/impersonate/:urn',
				element: (
					<ImpersonateLogged />
				),
			},
			...Object.values(privateModules).flatMap((module: any) => module.default),
		],
		errorElement: <RouteErrorPage />,
	},
	{
		element: <AuthLayout onLocationChange={notificationsSidebar.watchTrigger} />,
		children: [
			{
				path: '/editor/:projectId',
				element: <EditorPage />,
			},
			{
				path: '/validate_email/:token',
				element: <ValidateEmailChangePage />,
			},
			{
				path: '/invitations/:token',
				element: <InvitationAcceptPage />,
			},
		],
	},
]);

const createNoAccountRouter = () => createBrowserRouter([
	{
		element: (
			<AuthLayout
				renderHeader={() => <LayoutHeader />}
			/>
		),
		children: [
			{
				path: '/impersonate/:urn',
				element: (
					<ImpersonateLogged />
				),
			},
			{
				path: '*',
				element: (
					<AccountWall />
				),
			},
		],
		errorElement: <RouteErrorPage />,
	},
	{
		element: <AuthLayout />,
		children: [
			{
				path: '/invitations/:token',
				element: <InvitationAcceptPage />,
			},
		],
	},
]);

type RouterKey = 'public' | 'no-account' | 'private';

const ROUTER_FACTORIES: Record<RouterKey, () => ReturnType<typeof createBrowserRouter>> = {
	'public': createPublicRouter,
	'no-account': createNoAccountRouter,
	'private': createPrivateRouter,
};

export default function RouterProvider() {
	const { token, isLogging, logout } = useAuth();
	const { account, isSelectingAccount } = useAccount();

	// Les liens de réinitialisation de mot de passe (reçus par email) pointent vers des
	// pages publiques (/change_password/:token, /reset_password). Si l'utilisateur est
	// déjà connecté, alors le routeur privé ne connaît pas ces routes et affiche une 404. 
	// On force donc une déconnexion pour repartir d'une session vierge et basculer sur le routeur public.
	useEffect(() => {
		const { pathname } = window.location;
		const isPasswordResetLink = pathname.startsWith('/change_password/') || pathname === '/reset_password';

		if (token && isPasswordResetLink) {
			logout();
		}
	}, []);

	const routerKey: RouterKey | null = !token
		? 'public'
		: isLogging || (!account && isSelectingAccount) ? null : account ? 'private' : 'no-account';

	// Un routeur créé d'avance garderait l'adresse du chargement de la page et réafficherait, à son
	// activation, une page déjà quittée.
	const router = useMemo(() => routerKey ? ROUTER_FACTORIES[routerKey]() : null, [routerKey]);

	useEffect(() => () => router?.dispose(), [router]);

	return router ? <ReactRouterProvider key={routerKey} router={router} /> : <LoadingPage />;
}

const LoadingPage = () => {
	const { logout } = useAuth();
	const theme = useTheme();
	const tokens = useDesignTokens();

	return (
		<View height="100vh" center gap={20} bg={theme.colorBgContainer} color={theme.colorTextBase}>
			<LogoLoader size={64} />

			<View color={tokens.text.secondary} size={16} style={{ letterSpacing: '.02em' }}>
				Chargement de l'application
			</View>

			<Button type="link" onClick={() => logout()}>Déconnexion</Button>
		</View>
	);
};
